/**
 * ReportingSectionsPlan — the "Audit plan" and "Cycle times" sections of the
 * Reporting tab. Plan: quarters × planned/started/completed/set-aside as
 * grouped inline-SVG bars plus a table. Cycle times: median / p90 / n per
 * lifecycle step, with a per-audit drilldown behind a toggle.
 */
import React, { useState } from 'react';
import { Section, MicroLabel, useAnalytics, fmtDays, fmtPct } from './ReportingShared.jsx';
import { GroupedBars } from './ReportingCharts.jsx';
import { CYCLE_MEASURE_LABELS } from './labels.js';

const PLAN_SERIES = [
  { key: 'planned', label: 'Planned', color: '#cbd5e1' },
  { key: 'started', label: 'Started', color: '#fb923c' },
  { key: 'completed', label: 'Completed', color: '#10b981' },
  { key: 'dismissed', label: 'Set aside', color: '#94a3b8' },
];

export function PlanSection({ qs }) {
  const { loading, error, data } = useAnalytics('plan', qs);
  const plan = data?.plan;
  const quarters = plan?.quarters || [];
  const totals = plan?.totals;
  return (
    <Section
      title={`Audit plan${plan?.year ? ` · ${plan.year}` : ''}`}
      explainer="How the year's work plan is tracking, quarter by quarter, from planned through completed."
      loading={loading} error={error} empty={!quarters.length}
      emptyText="Nothing on the plan for this period yet. Plan items come from the COA work plan, the issues log, and the risk assessment."
    >
      <GroupedBars
        groups={quarters.map(r => ({ label: r.quarter, values: PLAN_SERIES.map(s => r[s.key]) }))}
        series={PLAN_SERIES}
      />
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <th className="py-1 pr-3">Quarter</th>
              {PLAN_SERIES.map(s => <th key={s.key} className="py-1 pr-3 text-right">{s.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {quarters.map(r => (
              <tr key={r.quarter} className="border-t border-slate-100 text-slate-700">
                <td className="py-1.5 pr-3 font-semibold">{r.quarter}</td>
                {PLAN_SERIES.map(s => <td key={s.key} className="py-1.5 pr-3 text-right tabular-nums">{r[s.key]}</td>)}
              </tr>
            ))}
            {totals && (
              <tr className="border-t border-slate-200 font-bold text-slate-800">
                <td className="py-1.5 pr-3">Total</td>
                {PLAN_SERIES.map(s => <td key={s.key} className="py-1.5 pr-3 text-right tabular-nums">{totals[s.key]}</td>)}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

export function CycleTimesSection({ qs }) {
  const { loading, error, data } = useAnalytics('cycle-times', qs);
  const [showDetail, setShowDetail] = useState(false);
  const ct = data?.cycle_times;
  const aggregates = ct?.aggregates || {};
  const measures = Object.entries(aggregates); // server sends CYCLE_MEASURES order
  const measured = measures.filter(([, m]) => Number(m?.n) > 0);
  const onTime = ct?.analysis_on_time;
  const audits = ct?.audits || [];
  return (
    <Section
      title="Cycle times against the CMS clock"
      explainer="How many days each step of the audit takes, median and 90th percentile, measured the way CMS runs its own program-audit timeline."
      loading={loading} error={error} empty={!measured.length}
      emptyText="No completed steps to time yet. As audits move through the lifecycle, each step's duration lands here."
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <th className="py-1 pr-3">Step</th>
              <th className="py-1 pr-3 text-right">Median</th>
              <th className="py-1 pr-3 text-right">90th percentile</th>
              <th className="py-1 pr-3 text-right">Audits measured</th>
            </tr>
          </thead>
          <tbody>
            {measured.map(([key, m]) => (
              <tr key={key} className="border-t border-slate-100 text-slate-700">
                <td className="py-1.5 pr-3">{CYCLE_MEASURE_LABELS[key] || m.label || key}</td>
                <td className="py-1.5 pr-3 text-right font-semibold tabular-nums">{fmtDays(m.median_days)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{fmtDays(m.p90_days)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums text-slate-500">{m.n}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {onTime && onTime.received_with_due > 0 && (
        <div className="text-[11px] text-slate-500 mt-2">
          Root cause and impact analyses returned by their due date: <b>{onTime.on_time} of {onTime.received_with_due}</b> ({fmtPct(onTime.rate)}).
        </div>
      )}
      <button onClick={() => setShowDetail(d => !d)} className="mt-3 text-[11px] font-semibold text-orange-600 hover:underline">
        {showDetail ? 'Hide per-audit detail' : 'Show per-audit detail'}
      </button>
      {showDetail && (
        <div className="overflow-x-auto mt-2">
          <MicroLabel>Days per step, one row per audit</MicroLabel>
          {audits.length === 0 ? (
            <div className="text-xs text-slate-400 py-2">No audits in this period.</div>
          ) : (
            <table className="w-full text-[11px] border-collapse">
              <thead>
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  <th className="py-1 pr-3">Audit</th>
                  {measures.map(([key, m]) => <th key={key} className="py-1 pr-3 text-right whitespace-nowrap">{(CYCLE_MEASURE_LABELS[key] || m.label || key)}</th>)}
                </tr>
              </thead>
              <tbody>
                {audits.map(a => (
                  <tr key={a.audit_id} className="border-t border-slate-100 text-slate-600">
                    <td className="py-1 pr-3 font-semibold text-slate-700 whitespace-nowrap">{a.subject_name}</td>
                    {measures.map(([key]) => <td key={key} className="py-1 pr-3 text-right tabular-nums">{fmtDays(a[key])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Section>
  );
}
